import React from 'react';
import AgTechViewDialog from '@agtech/web/components/Portals/Dialogs/AgTechViewDialog';
import { AgTechActionButton, AgTechButton, AgTechButtonProps, AgTechClickEventHandler } from '@agtech/web/components/Buttons/AgTechButtons';
import AgTechGrid from '@agtech/web/components/Grids/AgTechGrid';
import { AgTechGridEntitySelection } from '@agtech/web/components/Grids/AgTechGridEntities';
import { AgTechGridProps } from '@agtech/web/components/Grids/Data/AgTechGridProps';
import { AgTechVerticalPopUpMenu, AgTechVerticalPopUpMenuSection } from '@agtech/web/components/Menus/AgTechVerticalPopUpMenu';
import { AgTechQueryProjection, useAgTechQuery } from '@agtech/core/data/queries/AgTechDataQueries';
import { useDataActionExecutor } from '@agtech/core/data/actions/AgTechDataActions';
import { ApplicationRole } from '@sales/data/operation/roles/OperationRoleData';
import { OperationDataGetRolesQuery } from '@sales/data/operation/roles/OperationRoleQueries';
import { useOperationRoleAssignAction, useOperationRoleRemoveAction } from '@sales/data/operation/roles/OperationRoleActions';
import RoleClaimsDialog from './RoleClaimsDialog';

export type RoleManagementDialogProps = {
    userId: number,
    userName: string,
    roleIds: number[]
}

const roleSelectionQuery: AgTechQueryProjection<{ roleIds: number[] }, ApplicationRole[], { items: AgTechGridEntitySelection<ApplicationRole>[] }> = {
    query: OperationDataGetRolesQuery,
    projection: (props, roles) => ({
        items: roles.map<AgTechGridEntitySelection<ApplicationRole>>(role => ({
            id: role.id,
            entity: role,
            isSelected: props.inputData.roleIds.includes(role.id),
            wasOriginallySelected: props.inputData.roleIds.includes(role.id)
        }))
    })
}

const RoleManagementDialog = (props: RoleManagementDialogProps) => {
    const query = useAgTechQuery({
        query: roleSelectionQuery,
        queryContext: { roleIds: props.roleIds }
    });

    const assignAction = useOperationRoleAssignAction();
    const removeAction = useOperationRoleRemoveAction();
    const actionExecutor = useDataActionExecutor();

    const roleGridProps: AgTechGridProps<AgTechGridEntitySelection<ApplicationRole>> = {
        header: {
            title: 'Roles'
        },
        data: {
            itemQuery: query
        },
        columns: [
            {
                header: 'Name',
                value: r => r.entity.roleName
            }
        ],
        isRowEntityTheSame: (prev, curr) => prev.id === curr.id && prev.isSelected === curr.isSelected,
        actions: {
            rendering: r => (
                <AgTechActionButton>
                    <AgTechVerticalPopUpMenu>
                        <AgTechVerticalPopUpMenuSection header="Role">
                            <RoleClaimsDialog.Button applicationRole={r.entity} />
                        </AgTechVerticalPopUpMenuSection>
                    </AgTechVerticalPopUpMenu>
                </AgTechActionButton>
            )
        },
        selectionConfig: {
            isItemSelected: r => r.isSelected,
            onItemSelected: async r => {
                await actionExecutor.executeAction(assignAction, {
                    submittedEntity: { userId: props.userId, roleId: r.entity.id }
                });
            },
            onItemDeSelected: async r => {
                await actionExecutor.executeAction(removeAction, {
                    submittedEntity: { userId: props.userId, roleId: r.entity.id }
                });
            }
        }
    };

    return (
        <AgTechViewDialog
            structure={{
                header: () => ({
                    header: `Manage Roles - ${props.userName}`
                }),
                width: '500px'
            }}
        >
            {() => <AgTechGrid {...roleGridProps} />}
        </AgTechViewDialog>
    )
};

RoleManagementDialog.ButtonProps = (props: RoleManagementDialogProps): AgTechButtonProps => ({
    text: 'Manage Roles',
    icon: 'fas fa-user-tag',
    permission: 'operation.users.manage',
    action: (e: AgTechClickEventHandler) => e.dialogs.openDialog({
        dialog: () => <RoleManagementDialog {...props} />
    })
});

RoleManagementDialog.Button = (props: RoleManagementDialogProps & AgTechButtonProps) => (
    <AgTechButton {...RoleManagementDialog.ButtonProps(props)} {...props} />
);

export default RoleManagementDialog;
